import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SplittingSuggestion, StratifyDocument } from "@stratify/shared"; // Import SplittingSuggestion
import { DocumentsState } from "../types";

const initialState: DocumentsState = {
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  filters: {
    search: "",
    address: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  },
  suggestionsByDocId: {}, // Initialize the new state field
};

const documentsSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    documentAdded: (state, action: PayloadAction<StratifyDocument>) => {
      state.byId[action.payload.id] = action.payload;
      if (!state.allIds.includes(action.payload.id)) {
        state.allIds.push(action.payload.id);
      }
    },
    documentModified: (state, action: PayloadAction<StratifyDocument>) => {
      if (state.byId[action.payload.id]) {
        state.byId[action.payload.id] = action.payload;
      }
    },
    documentRemoved: (state, action: PayloadAction<string>) => {
      const { [action.payload]: _, ...remainingDocs } = state.byId;
      state.byId = remainingDocs;
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    fetchDocumentsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDocumentsSuccess: (
      state,
      action: PayloadAction<StratifyDocument[]>
    ) => {
      state.loading = false;
      state.byId = action.payload.reduce(
        (acc, doc) => {
          acc[doc.id] = doc;
          return acc;
        },
        {} as Record<string, StratifyDocument>
      );
      state.allIds = action.payload.map((doc) => doc.id);
    },
    fetchDocumentsError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    deleteDocumentStart: (
      state,
      _action: PayloadAction<{ documentId: string; filename: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    deleteDocumentSuccess: (
      state,
      action: PayloadAction<{ documentId: string }>
    ) => {
      state.loading = false;
      const { [action.payload.documentId]: _, ...remainingDocs } = state.byId;
      state.byId = remainingDocs;
      state.allIds = state.allIds.filter(
        (id) => id !== action.payload.documentId
      );
    },
    deleteDocumentError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateFilters: (
      state,
      action: PayloadAction<Partial<DocumentsState["filters"]>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };

      if (state.filters.sortBy == undefined) {
        state.filters.sortBy = "createdAt";
      }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    updateCategoryStart: (
      state,
      _action: PayloadAction<{
        documentId: string;
        categoryId: string;
        rerunInsights?: boolean;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateCategorySuccess: (
      state,
      action: PayloadAction<{ document: StratifyDocument }>
    ) => {
      state.loading = false;
      // Update the document in the state
      const { document } = action.payload;
      state.byId[document.id] = document;
    },
    updateCategoryFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Document address update actions
    updateDocumentAddressStart: (
      state,
      _action: PayloadAction<{ documentId: string; address: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateDocumentAddressSuccess: (
      state,
      action: PayloadAction<{ document: StratifyDocument }>
    ) => {
      state.loading = false;
      const { document } = action.payload;
      state.byId[document.id] = document;
    },
    updateDocumentAddressFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Document strata plan update actions
    updateDocumentStrataPlanStart: (
      state,
      _action: PayloadAction<{ documentId: string; strataPlanNumber: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateDocumentStrataPlanSuccess: (
      state,
      action: PayloadAction<{ document: StratifyDocument }>
    ) => {
      state.loading = false;
      const { document } = action.payload;
      state.byId[document.id] = document;
    },
    updateDocumentStrataPlanFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Splitting Suggestions Actions
    fetchSuggestionsStart: (state, action: PayloadAction<string>) => {
      const documentId = action.payload;
      state.suggestionsByDocId[documentId] = {
        ...(state.suggestionsByDocId[documentId] || { data: null }), // Preserve existing data if any
        loading: true,
        error: null,
      };
    },
    fetchSuggestionsSuccess: (
      state,
      action: PayloadAction<{
        documentId: string;
        suggestions: SplittingSuggestion[];
      }>
    ) => {
      const { documentId, suggestions } = action.payload;
      state.suggestionsByDocId[documentId] = {
        data: suggestions,
        loading: false,
        error: null,
      };
    },
    fetchSuggestionsError: (
      state,
      action: PayloadAction<{ documentId: string; error: string }>
    ) => {
      const { documentId, error } = action.payload;
      state.suggestionsByDocId[documentId] = {
        ...(state.suggestionsByDocId[documentId] || { data: null }), // Preserve existing data if any
        loading: false,
        error: error,
      };
    },
  },
});

export const {
  documentAdded,
  documentModified,
  documentRemoved,
  fetchDocumentsStart,
  fetchDocumentsSuccess,
  fetchDocumentsError,
  deleteDocumentStart,
  deleteDocumentSuccess,
  deleteDocumentError,
  updateFilters,
  resetFilters,
  updateCategoryStart,
  updateCategorySuccess,
  updateCategoryFailure,
  updateDocumentAddressStart,
  updateDocumentAddressSuccess,
  updateDocumentAddressFailure,
  updateDocumentStrataPlanStart,
  updateDocumentStrataPlanSuccess,
  updateDocumentStrataPlanFailure,
  // Export new actions
  fetchSuggestionsStart,
  fetchSuggestionsSuccess,
  fetchSuggestionsError,
} = documentsSlice.actions;

export const selectIsCategoryUpdating = (state: {
  documents: DocumentsState;
}) => state.documents.loading;

export default documentsSlice.reducer;
